import React, { createContext, useState, useEffect, useCallback } from "react";

export const AppContext = createContext();

const initialUser = {
  email: null,
  username: null,
  isSuperuser: false,
  isAdmin: false,
  isVendedor: false,
  isLoggedIn: false,
};

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) {
      return decodeURIComponent(t.substring(name.length + 1));
    }
  }
  return "";
};

const fetchCsrfToken = async () => {
  const fromCookie = getCSRFTokenFromCookie();
  if (fromCookie) return fromCookie;

  try {
    const r = await fetch("/sistema/get-csrf-token/", {
      method: "GET",
      credentials: "include",
    });
    const d = await r.json().catch(() => ({}));
    return d?.csrfToken || d?.csrf || d?.token || "";
  } catch {
    return "";
  }
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(initialUser);
  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    const u = {
      email: localStorage.getItem("user_email"),
      username: localStorage.getItem("user_name"),
      isSuperuser: localStorage.getItem("is_superuser") === "true",
      isAdmin: localStorage.getItem("is_admin") === "true",
      isVendedor: localStorage.getItem("is_vendedor") === "true",
      isLoggedIn: localStorage.getItem("is_logged_in") === "true",
    };
    setUser((prev) => ({ ...prev, ...u }));
  }, []);

  const loginUser = ({ email, username, isSuperuser, isAdmin, isVendedor }) => {
    const payload = {
      email: email || null,
      username: username || null,
      isSuperuser: !!isSuperuser,
      isAdmin: !!isAdmin,
      isVendedor: !!isVendedor,
      isLoggedIn: true,
    };

    setUser(payload);
    localStorage.setItem("user_email", payload.email ?? "");
    localStorage.setItem("user_name", payload.username ?? "");
    localStorage.setItem("is_superuser", String(payload.isSuperuser));
    localStorage.setItem("is_admin", String(payload.isAdmin));
    localStorage.setItem("is_vendedor", String(payload.isVendedor));
    localStorage.setItem("is_logged_in", "true");
  };

  const logoutUser = () => {
    setUser(initialUser);
    setSections([]);
    setBrands([]);
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_name");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_vendedor");
    localStorage.removeItem("is_logged_in");
  };

  const refreshSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const response = await fetch("/categorias/secciones/", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        setSections([]);
        return;
      }

      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch {
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  const refreshBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const response = await fetch("/categorias/marcas/", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        setBrands([]);
        return;
      }

      const data = await response.json();
      setBrands(Array.isArray(data) ? data : []);
    } catch {
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const csrfFetch = useCallback(async (url, options = {}) => {
    const method = (options.method || "GET").toUpperCase();
    const csrfSafe = ["GET", "HEAD", "OPTIONS", "TRACE"].includes(method);
    const csrfToken = csrfSafe ? "" : await fetchCsrfToken();

    const headers = new Headers(options.headers || {});
    if (!csrfSafe && csrfToken) {
      headers.set("X-CSRFToken", csrfToken);
    }

    return fetch(url, {
      credentials: "include",
      ...options,
      headers,
    });
  }, []);

  useEffect(() => {
    refreshSections();
    refreshBrands();
  }, [refreshSections, refreshBrands]);

  return (
    <AppContext.Provider
      value={{
        user,
        loginUser,
        logoutUser,
        sections,
        setSections,
        brands,
        setBrands,
        refreshSections,
        refreshBrands,
        sectionsLoading,
        brandsLoading,
        csrfFetch,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};